import {
  Image,
  withStory,
  WithStoryProps,
} from '@storyofams/storyblok-toolkit';
import { GetStaticPaths, GetStaticProps } from 'next';
import SbEditable from 'storyblok-react';

import { Block, Heading, Logo, Seo } from '~/components';
import { Form } from '~/components/Forms';
import {
  addFormFieldsToStory,
  enhancedStaticProps,
  getSlugWithLocale,
  sdk as sdkClient,
  withLayout,
} from '~/lib';

export interface WhitepaperProps extends WithStoryProps {}

const Whitepaper = ({ story }: WhitepaperProps) => {
  const content = story?.content;

  return (
    <>
      <Seo story={story} />

      <SbEditable content={story?.content}>
        <div className="w-full mt-24 overflow-hidden relative">
          {!!content?.bg_image?.filename && (
            <div className="w-full absolute bottom-0 left-0 right-0 z-0">
              <Image
                alt={content?.bg_image?.alt || ''}
                src={content?.bg_image?.filename}
                fit="contain"
                showPlaceholder={false}
                width="100%"
              />
            </div>
          )}
          <div className="w-full max-w-6xl px-4 mx-auto pb-20 lg:py-20 relative z-20 flex flex-col lg:flex-row-reverse lg:justify-between lg:items-center">
            <div className="bg-white rounded-2xl w-full max-w-[492px] flex justify-center items-end pt-10 lg:pt-0 overflow-hidden mx-auto lg:mx-0 mb-14 lg:mb-0">
              {!!content?.thumbnail?.filename && (
                <div className="w-full max-w-[326px] shadow-dark-lg">
                  <Image
                    alt={content?.thumbnail?.alt || ''}
                    src={content?.thumbnail?.filename}
                    fit="contain"
                    showPlaceholder={false}
                    width="100%"
                  />
                  <div className="p-8">
                    <div className="font-bold">{content?.title}</div>
                    <Logo className="w-20 mt-12" />
                  </div>
                </div>
              )}
            </div>

            <div className="max-w-[480px] mx-auto lg:mx-0">
              {!!content?.title && (
                <Heading as="h1" variant="h2">
                  {content?.title}
                </Heading>
              )}
              {!!content?.subtitle && (
                <p className="text-lg mt-4 text-gray-700">
                  {content?.subtitle}
                </p>
              )}

              {!!content?.form_id && (
                <Form content={content as any} className="mt-10" />
              )}
            </div>
          </div>
        </div>
      </SbEditable>

      {!!content?.blocks?.[0]?.component &&
        content?.blocks?.map((block) => {
          return (
            <Block
              key={block?._uid}
              component={story?.content?.component}
              sectionType={block?.component}
              content={block}
              title={story?.content?.seo?.title || story?.content?.title}
            />
          );
        })}
    </>
  );
};

export default withLayout(withStory(Whitepaper));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ params: { slug }, sdk, locale, defaultLocale }) => {
    let story;
    let notFound = false;

    try {
      story = (
        await sdk.whitepaperItem({
          slug: getSlugWithLocale(`whitepapers/${slug}`, locale, defaultLocale),
          lang: locale,
        })
      ).WhitepaperItem;
      story = await addFormFieldsToStory(story);
    } catch (e) {
      notFound = true;
    }

    return {
      props: {
        story,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);

export const getStaticPaths: GetStaticPaths = async () => {
  let stories;

  try {
    stories = (await sdkClient.whitepaperItems()).WhitepaperItems.items;
  } catch (e) {
    console.log(e);
  }

  return {
    paths: stories?.map(({ slug }) => {
      return {
        params: { slug },
      };
    }),
    fallback: 'blocking',
  };
};
